/**
 * Display formatting.
 *
 * Money arrives from the server as a decimal string ("5886.00") and is only
 * ever formatted here — never added, subtracted or compared as a number. The
 * server has already done every calculation; turning these back into floats
 * would reintroduce exactly the imprecision it avoided.
 */

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/** Escapes text before it goes into innerHTML. Every dynamic value uses this. */
export function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ESCAPES[character] ?? character)
}

function split(value: string): { sign: string; whole: string; fraction: string } {
  const negative = value.startsWith('-')
  const [whole = '0', fraction = '00'] = value.replace('-', '').split('.')
  return { sign: negative ? '−' : '', whole, fraction: fraction.padEnd(2, '0').slice(0, 2) }
}

/** "$5,886.00". Always two decimals, as the weekly plan shows. */
export function money2(value: string): string {
  const { sign, whole, fraction } = split(value)
  return `${sign}$${Number(whole).toLocaleString('en-US')}.${fraction}`
}

/** "$29,430" when whole, "$561.95" when not — the prototype's headline style. */
export function money(value: string): string {
  const { sign, whole, fraction } = split(value)
  const grouped = Number(whole).toLocaleString('en-US')
  return fraction === '00' ? `${sign}$${grouped}` : `${sign}$${grouped}.${fraction}`
}

/** True when a money string is greater than zero, without parsing it as a float. */
export function isPositive(value: string): boolean {
  return !value.startsWith('-') && /[1-9]/.test(value)
}

export function isNegative(value: string): boolean {
  return value.startsWith('-') && /[1-9]/.test(value)
}

/** Compares two money strings. Returns true when a is less than b. */
export function isLessThan(a: string, b: string): boolean {
  return cents(a) < cents(b)
}

/**
 * Whole cents as a number.
 *
 * For comparison and for working out how wide a progress bar should be —
 * never for arithmetic whose result is shown as money. The server owns every
 * figure the user reads.
 */
export function cents(value: string): number {
  const { sign, whole, fraction } = split(value)
  const total = Number(whole) * 100 + Number(fraction)
  return sign ? -total : total
}

/** "Sep 4" — dates arrive as 'YYYY-MM-DD' and carry no time zone. */
export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${iso}T00:00:00Z`))
}

/** "September 2026" */
export function monthLong(month: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${month}-01T00:00:00Z`))
}

/** "September" */
export function monthName(month: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long' }).format(
    new Date(`${month}-01T00:00:00Z`),
  )
}

export function addMonths(month: string, count: number): string {
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7)) - 1 + count
  const shifted = new Date(Date.UTC(year, index, 1))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
}

/** The current month in the business time zone, as 'YYYY-MM'. */
export function currentMonth(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
  })
    .format(new Date())
    .slice(0, 7)
}

/** Renders whole cents back to a decimal string. */
export function fromCents(total: number): string {
  const sign = total < 0 ? '-' : ''
  const absolute = Math.abs(Math.trunc(total))
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`
}

/**
 * Adds money strings exactly, via whole cents.
 *
 * Used only for column totals of figures the server already produced, so no
 * cent is invented here — and going through cents rather than floats keeps a
 * footer agreeing with the rows above it.
 */
export function addMoney(...values: string[]): string {
  let total = 0
  for (const value of values) total += cents(value)
  return fromCents(total)
}

/** Subtracts money strings exactly. Same rule as addMoney. */
export function subtractMoney(a: string, b: string): string {
  return fromCents(cents(a) - cents(b))
}
